import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { toast } from '@/lib/hooks/useToast.ts'
import i18n from '@/i18n.ts'
import { LOCAL_STORAGE_LANG_KEY } from '@/lib/constants/constants.ts'
import { shouldQueueMutation } from '@/lib/platform/platformDetection'
import { nativeQueueManager } from '@/lib/nativeSync/nativeQueueManager'
// Network status reads go through the platform wrapper instead of
// directly to `@capacitor/network` so the dev harness can override
// them (see `lib/platform/networkStatus.ts`). In production builds the
// wrapper is a thin pass-through; the harness override + its import
// are tree-shaken away.
import { getStatus as getNetworkStatus } from '@/lib/platform/networkStatus'
import { getApiBaseUrl } from '@/config/apiConfig'
import { slowConnectionDetector } from '@/lib/slowConnectionDetector'
import { hasPendingQueueEntriesForType } from '@/lib/helpers/bgSync/pendingQueueState'
import { shouldRedirectUnauthorizedToLogin } from '@/lib/publicRoutePolicy'
import { reportHttpFailure } from '@/lib/monitoring/httpFailureReporting'
// Dev-harness network override. The import is only USED inside an
// `if (__DEV_HARNESS__) { ... }` block below, so Rollup tree-shakes both
// the symbol use AND this import in production builds — the harness
// module + its zustand dependency are not in `dist/`. Asserted by
// `verify-prod-build`. The transport module is side-effect-free at
// module load (it just exports a getter), which is what makes the
// tree-shake clean.
import { getHarnessAxiosOverride } from '@/lib/devtools/harness/domains/offline/networkTransport'
// Dev-harness mock-202 bus. Same DCE story as the network override —
// only USED inside an `if (__DEV_HARNESS__) { ... }` block in
// `handleNativeQueueRequest` below, so the symbols + the import + the
// bus module + its zustand state are all tree-shaken from production.
// The bus records every mock-202 emission and (optionally) wraps the
// response `data` in a Proxy that flags reads of keys outside
// `{status, metadata}` — that's the "mock-202 cache trap" failure
// category from `audit/domains/offline.md`.
import { emitMock202, wrapMock202Data } from '@/lib/devtools/harness/domains/offline/mock202Bus'

const isDev = import.meta.env.DEV
const debug = (...args: unknown[]) => isDev && console.log('[API]', ...args)

/**
 * Map API URL segments to React Query cache keys that should be invalidated
 * when a mutation (POST/PATCH/DELETE) succeeds on that endpoint.
 * Broad invalidation is intentional — ensures all related views stay fresh.
 * Add new entries here when adding new API resources or query keys.
 */
const MUTATION_INVALIDATION_MAP: Record<string, string[]> = {
  '/issues': [
    'singleIssue',
    'stageIssuesByIssueType',
    'singleReportIssues',
    'getIssuesSummary',
    'getIssueTypeArchiveQuery',
  ],
  '/comments': ['singleIssueComments', 'singleIssueEvents'],
  '/media_objects': ['singleIssue', 'singleReportIssues'],
  '/reports': ['singleReport', 'singleReportIssues', 'getReportsQuery'],
  '/notifications': [
    'getNotificationsSummary',
    'getNotificationsByStageId',
    'getUnreadNotificationsByStageId',
  ],
  '/locations': ['stageLocations', 'getProjectStageLocations'],
  '/statuses': ['projectStatusGroups', 'organizationStatusGroups'],
  '/status_groups': ['projectStatusGroups', 'organizationStatusGroups'],
  '/labels': ['projectLabelGroups', 'organizationLabelGroups'],
  '/label_groups': ['projectLabelGroups', 'organizationLabelGroups'],
  '/issue_types': ['projectIssueTypes', 'organizationIssueTypes', 'currStageIssueTypes'],
  '/project_stages': ['orgProjectStages', 'currStageUsersQuery', 'currStageIssueTypes'],
  '/projects': ['project', 'orgProjectStages', 'orgProjects'],
  '/project_participants': ['getProjectStageParticipations', 'currStageUsersQuery'],
  '/project_participations': ['getProjectStageParticipations', 'userProjectParticipations'],
  '/project_roles': ['currStageIssueTypes'],
  '/organization_members': ['getOrgMembers'],
  '/organizations': ['currentOrganization'],
  '/users': ['user', 'getOrgMembers'],
  '/custom_issue_filters': ['currStageIssueTypes'],
}

/** Mutation URLs that should NOT trigger auto-invalidation (async operations, exports, etc.) */
const MUTATION_INVALIDATION_SKIP: string[] = ['/export-pdf', '/login', '/reset_password']

/**
 * URL segments that route through the SW / native sync queue, paired with the
 * `meta.type` they queue under. When a non-queued method (DELETE today) lands
 * on one of these endpoints while pending entries still exist for that type,
 * the per-response invalidation below is suppressed: refetching now would
 * race the in-flight queue and clobber optimistic state painted by the
 * caller. `useQueueDrainInvalidator` runs a single invalidation once the
 * queue empties, which becomes the canonical reconciliation point.
 */
const QUEUED_ENDPOINT_TYPES: Array<readonly [string, string]> = [
  ['/issues', 'issues'],
  ['/comments', 'comments'],
  ['/media_objects', 'media_objects'],
  ['/locations', 'locations'],
]

class NativeQueuedResponse {
  constructor(
    public readonly response: {
      data: { status: string; metadata: unknown }
      status: number
      statusText: string
      headers: Record<string, string>
      config: InternalAxiosRequestConfig
    },
    public readonly config: InternalAxiosRequestConfig,
  ) {}
}

/**
 * Check if an AxiosError is caused by being offline / no network.
 * These errors have no response (request never reached server) and should
 * not show a toast – the user already knows they're offline.
 */
const isOfflineOrTimeoutError = (error: AxiosError): boolean =>
  !navigator.onLine ||
  error.code === 'ERR_NETWORK' ||
  error.code === 'ECONNABORTED' ||
  error.message === 'Network Error'

/**
 * True when a response is a maintenance-mode 503 (carries X-Maintenance-Mode).
 * Read case-safely: axios may expose response headers as an AxiosHeaders
 * instance (case-insensitive `.get`) or a plain lowercased-key object.
 */
const isMaintenance503 = (error: AxiosError): boolean => {
  if (error.response?.status !== 503) return false
  const headers = error.response.headers as unknown as {
    get?: (name: string) => unknown
    [key: string]: unknown
  }
  const raw =
    typeof headers?.get === 'function'
      ? headers.get('X-Maintenance-Mode')
      : headers?.['x-maintenance-mode']
  return String(raw ?? '').toLowerCase() === 'true'
}

const SEVEN_DAYS = 1000 * 60 * 60 * 24 * 7

const api = axios.create({
  baseURL: getApiBaseUrl() || '/',
  withCredentials: true,
  timeout: 15_000,
})

api.defaults.headers.common['Forward-To-Api'] = 'true'

api.defaults.headers.get['Accept'] = 'application/ld+json'

api.defaults.headers.patch['Accept'] = 'application/ld+json'
api.defaults.headers.patch['Content-Type'] = 'application/merge-patch+json'

api.defaults.headers.post['Accept'] = 'application/ld+json'
api.defaults.headers.post['Content-Type'] = 'application/ld+json'

import { isQueueableUrl, QUEUE_BYPASS_PATTERNS } from '@/lib/queueBypass'
// Re-export for callers that previously imported these from api-client.
export { isQueueableUrl, QUEUE_BYPASS_PATTERNS }

const checkNetworkConnected = async (): Promise<boolean> => {
  try {
    const status = await getNetworkStatus()
    return status.connected
  } catch {
    return navigator.onLine
  }
}

// Always queue POST/PATCH requests first, then immediately sync if online
const handleNativeQueueRequest = async (
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> => {
  const method = config.method?.toUpperCase()
  const url = config.url || ''
  const isQueueable = isQueueableUrl(url)

  if ((method === 'POST' || method === 'PATCH') && isQueueable) {
    debug('iOS native queuing request:', url)

    let formData: FormData | undefined
    if (config.data instanceof FormData) {
      formData = config.data
    }

    const result = await nativeQueueManager.enqueueRequest(config, formData)

    // Immediately trigger sync if online (non-blocking)
    checkNetworkConnected()
      .then(isConnected => {
        if (isConnected) {
          debug('Online, triggering immediate sync')
          nativeQueueManager.performSync().catch(err => {
            debug('Sync failed, will retry later:', err)
          })
        }
      })
      .catch(err => {
        debug('Network check failed:', err)
      })

    const mockResponse = {
      data: { status: result.status, metadata: result.metadata } as {
        status: string
        metadata: unknown
      },
      status: 202,
      statusText: 'Accepted',
      headers: {},
      config,
    }

    // Dev-harness observation: record the mock 202 in the bus and wrap
    // `data` in the Proxy that flags consumer reads of keys outside
    // `{status, metadata}`. The whole block + its imports + the bus
    // module are tree-shaken from production builds. `verify-prod-build`
    // asserts the bus's domain marker is absent from `dist/`.
    if (__DEV_HARNESS__) {
      const requestUUID =
        (result.metadata as { requestUUID?: string } | null | undefined)?.requestUUID ?? null
      const entry = emitMock202({
        ts: Date.now(),
        method: method as 'POST' | 'PATCH',
        url,
        requestUUID,
        status: result.status,
        metadata: result.metadata,
      })
      mockResponse.data = wrapMock202Data(mockResponse.data, entry)
    }

    return Promise.reject(new NativeQueuedResponse(mockResponse, config))
  }

  return config
}

// Track request start times for slow connection detection (WeakMap avoids config mutation)
const requestStartTimes = new WeakMap<InternalAxiosRequestConfig, number>()

/**
 * Single exit point for the slow-connection latency sample: consume the start
 * time stamped by the request interceptor and feed the elapsed time to the
 * detector — unless this particular request opted out.
 *
 * Both interceptors route through here so the opt-out cannot be honoured on
 * one path and forgotten on the other; a request that errors or times out is
 * not sampled either if it was opted out.
 *
 * `isRoundTrip` is what distinguishes the two callers: a response (any status)
 * and a timeout are real round-trips, a bare ERR_NETWORK is not.
 *
 * `skipLatencySampling` is declared on `AxiosRequestConfig` in
 * `lib/slowConnectionDetector.ts`, next to the thresholds it protects.
 * The start time is dropped either way — an opted-out request must not leave
 * a stale stamp behind.
 */
const settleLatencySample = (config: InternalAxiosRequestConfig, isRoundTrip: boolean): void => {
  const startTime = requestStartTimes.get(config)
  if (startTime === undefined) return
  requestStartTimes.delete(config)
  if (!isRoundTrip || config.skipLatencySampling) return
  slowConnectionDetector.recordLatency(Date.now() - startTime)
}

api.interceptors.request.use(async config => {
  requestStartTimes.set(config, Date.now())

  if (typeof window !== 'undefined') {
    config.headers['User-Locale'] = localStorage.getItem(LOCAL_STORAGE_LANG_KEY) || 'pl'

    const method = config.method?.toUpperCase()
    const url = config.url || ''
    const isQueueable = isQueueableUrl(url)
    const useQueue = shouldQueueMutation()

    debug(`Request: ${method} ${url}, isQueueable=${isQueueable}, useQueue=${useQueue}`)

    if (useQueue && (method === 'POST' || method === 'PATCH') && isQueueable) {
      debug('>>> Routing to native queue')
      return handleNativeQueueRequest(config)
    }

    // Dev-harness offline override. Queueable mutations have already
    // routed to the native queue above (they don't depend on network);
    // everything else — GETs, DELETEs, non-queueable POST/PATCH — would
    // otherwise hit the network adapter, and that's the surface this
    // override simulates as "no network."
    //
    // The whole block is dead code when `__DEV_HARNESS__` is false
    // (Vite `define` resolves it to the literal `false` at compile
    // time), so the symbol use + the static import at the top of this
    // file + the entire networkTransport / networkStore module graph
    // are tree-shaken from production builds. `verify-prod-build`
    // asserts no `getHarnessAxiosOverride` reference reaches `dist/`.
    if (__DEV_HARNESS__) {
      if (getHarnessAxiosOverride() === 'offline') {
        throw new AxiosError('Network Error (harness offline)', 'ERR_NETWORK', config)
      }
    }
  }
  return config
})

api.interceptors.response.use(
  response => {
    settleLatencySample(response.config, true)

    // Auto-invalidate React Query caches after successful mutations.
    // Queueable endpoints (/issues, /comments, /media_objects) are updated via
    // Mercure SSE after the Dexie queue syncs — no cache invalidation here.
    // All other endpoints: invalidate normally on successful mutation.
    const method = response.config.method?.toUpperCase()
    if (method && ['POST', 'PATCH', 'PUT', 'DELETE'].includes(method)) {
      const url = response.config.url ?? ''
      // Only suppress invalidation for methods actually routed through the
      // Dexie queue (POST/PATCH). DELETE goes straight to the server and
      // needs normal cache invalidation — Mercure alone is not sufficient
      // because the delete response arrives before the SSE event.
      const isQueuedMethod = method === 'POST' || method === 'PATCH'
      const isQueuedEndpoint = isQueuedMethod && isQueueableUrl(url)
      const shouldSkip = MUTATION_INVALIDATION_SKIP.some(skip => url.includes(skip))

      // Non-queued method (e.g. DELETE) landing on a queueable resource
      // while writes for that resource are still in flight: defer
      // invalidation. Refetching now races the draining queue and
      // overwrites optimistic state. `useQueueDrainInvalidator` will fire
      // a single invalidation once the queue is empty.
      const queuedType = QUEUED_ENDPOINT_TYPES.find(([seg]) => url.includes(seg))?.[1]
      const queueIsBusy = !!queuedType && hasPendingQueueEntriesForType(queuedType)

      if (!isQueuedEndpoint && !shouldSkip && !queueIsBusy) {
        for (const [segment, queryKeys] of Object.entries(MUTATION_INVALIDATION_MAP)) {
          if (url.includes(segment)) {
            for (const key of queryKeys) {
              queryClient.invalidateQueries({ queryKey: [key] })
            }
          }
        }
      }
    }

    return response
  },
  async error => {
    // Record latency for errors that reflect actual network round-trips:
    // - Responses (4xx/5xx): server was reached, latency is real
    // - Timeouts (ECONNABORTED): request hung, latency = timeout value
    // Skip ERR_NETWORK (truly offline) — not a latency signal.
    if (error instanceof AxiosError && error.config) {
      settleLatencySample(error.config, Boolean(error.response) || error.code === 'ECONNABORTED')
    }

    if (error instanceof NativeQueuedResponse) {
      debug('Native queue request accepted')
      return error.response
    }

    // Maintenance window: a non-team request was 503'd with the maintenance
    // marker. Flip the app into the overlay now (don't wait up to 30s for the
    // next poll) and stay quiet — no error toast, no /login redirect.
    if (error instanceof AxiosError && isMaintenance503(error)) {
      // keep key in sync with maintenanceStatusQueryKey in useMaintenanceStatus.tsx
      void queryClient.invalidateQueries({ queryKey: ['maintenanceStatus'] })
      return Promise.reject(error)
    }

    // 5xx and timeouts are otherwise invisible to BayaWatch when the UI
    // handles the rejection (see `httpFailureReporting.ts`). Placed after the
    // mock-202 and maintenance-503 exits so neither is ever reported.
    reportHttpFailure(error)

    if (error.response && error.request?.responseURL) {
      const relativePath = window.location.pathname + window.location.search + window.location.hash
      const loginPath = '/login'
      if (window.location.pathname === loginPath || error.request.responseURL.includes('/login')) {
        return Promise.reject(error)
      }

      if (error.response.status === 401) {
        const requestUrl = error.config?.url || error.request.responseURL
        if (shouldRedirectUnauthorizedToLogin(window.location.pathname, requestUrl)) {
          console.error(`redirecting to ${loginPath} for ${relativePath} due to 401`)
          window.location.href = loginPath
        }
        return Promise.reject(error)
      }

      // 403 on the auth endpoint means session expired (not a permission error on a resource)
      if (error.response.status === 403) {
        const requestUrl = error.config?.url || ''
        if (requestUrl.includes('/users/me')) {
          console.error(
            `redirecting to ${loginPath} for ${relativePath} due to 403 on auth endpoint`,
          )
          window.location.href = loginPath
          return Promise.reject(error)
        }
      }
    }
    return Promise.reject(error)
  },
)

export { api }

// React Query
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error: Error, query) => {
      debug('Query error:', error)
      if ((query.options as { throwOnError?: boolean }).throwOnError === false) return
      if (
        error instanceof AxiosError &&
        !isOfflineOrTimeoutError(error) &&
        !isMaintenance503(error)
      ) {
        toast({
          variant: 'destructive',
          title: i18n.t('common.error'),
          description: error?.response?.data?.detail,
        })
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error: Error) => {
      debug('Mutation error:', error)
      if (
        error instanceof AxiosError &&
        !isOfflineOrTimeoutError(error) &&
        !isMaintenance503(error)
      ) {
        // TODO(server-409): Server currently uses last-write-wins and never returns 409.
        // This handler is forward-looking plumbing — it will activate when the server
        // adds updatedAt/ETag conflict checking on PATCH endpoints.
        // NOTE: MutationCache.onError fires BEFORE per-mutation onError callbacks.
        // The `return` below prevents the generic toast but does NOT prevent
        // mutation-level error handlers from also firing. If individual mutation hooks
        // add their own error handling, they should also guard against 409.
        if (error.response?.status === 409) {
          toast({
            variant: 'destructive',
            title: i18n.t('conflict.title'),
            description: i18n.t('conflict.description'),
          })
          const requestUrl = error.config?.url || ''
          if (requestUrl.includes('/issues')) {
            queryClient.invalidateQueries({ queryKey: ['singleIssue'] })
            queryClient.invalidateQueries({ queryKey: ['stageIssuesByIssueType'] })
          }
          if (requestUrl.includes('/comments')) {
            queryClient.invalidateQueries({ queryKey: ['singleIssueComments'] })
          }
          return
        }
        toast({
          variant: 'destructive',
          title: i18n.t('common.error'),
          description: error?.response?.data?.detail,
        })
      }
    },
  }),
  defaultOptions: {
    mutations: {
      networkMode: 'always',
    },
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 3,
      // gcTime must be >= maxAge in persistOptions for offline support
      // This keeps cached data available for 7 days for offline access
      gcTime: SEVEN_DAYS,
      refetchOnReconnect: 'always',
      // Don't throw errors to error boundaries - handle via isError state instead
      // This prevents offline/network errors from crashing the app
      throwOnError: false,
      retry: false,
    },
  },
})
